import '@ant-design/compatible/assets/index.css';
import { useQuery } from 'react-query';
import React, { useCallback, useEffect, useMemo } from 'react';

import { Spin, InputNumber } from 'antd';
import DF from 'DisplayFramework';
import { toCurrency } from 'utils/utils';
import FormItem from 'antd/lib/form/FormItem';

import Form, { FormInstance } from 'antd/lib/form';

import { isNil } from 'lodash';
import DataGrid, { DataGridColDef } from 'components/DataGrid/DataGrid';
import { indexBy, shouldUpdateCompare } from 'utils/utils2';
import { useForm } from 'antd/lib/form/Form';
import { AssetResponse, getProposalById } from 'services/proposal';
import GetLabel from 'DisplayFramework/components/GetLabel';
import FormInput from 'components/FormInput';

export type TabSplitListAssetProps = {
  objectid: number;
  form: FormInstance;
  setSuccessful: any;
};

const TabSplitListAsset = ({
  objectid,
  form: formW,
  setSuccessful,
}: TabSplitListAssetProps) => {
  const fieldName = 'listassets';
  const [form] = useForm();

  const { data: proposal, isFetching } = useQuery(
    ['getProposalById', { ctrid: objectid }],
    () => getProposalById({ ctrid: objectid }).then((res) => res.data ?? {}),
    {
      enabled: !isNil(objectid),
    }
  );

  useEffect(() => {
    setSuccessful(isFetching);
  }, [isFetching]);

  const getName = useCallback(
    (name: string) => (record: AssetResponse) => {
      const assets = form.getFieldValue(fieldName);
      const index = indexBy(assets, 'assetid')[record.assetid];
      return [fieldName, index, name];
    },
    [form]
  );

  useEffect(() => {
    if (proposal) {
      form.setFieldsValue({
        [fieldName]: proposal.assets.map((asset) => ({
          ...asset,
          splitquantity: asset.assetqty,
        })),
      });
      formW?.setFieldsValue({
        splitassetlist: form.getFieldValue([fieldName]).map((asset: any) => ({
          assetid: asset.assetid,
          quantity: asset.splitquantity,
        })),
      });
    } else
      form.setFieldsValue({
        [fieldName]: [],
      });
  }, [objectid, proposal, form]);

  const columns = useMemo<DataGridColDef<AssetResponse>[]>(
    () => [
      {
        dataIndex: 'assetreference',
        width: 140,
      },

      {
        dataIndex: 'assetlabel',
        width: 250,
      },
      {
        dataIndex: 'assettypelabel',
        width: 200,
      },
      {
        dataIndex: 'assetcategorylabel',
        width: 200,
      },
      {
        dataIndex: 'suppliername',
        width: 250,
      },

      {
        dataIndex: 'statuslabel',
        width: 100,
      },
      {
        dataIndex: 'assetdeliverydate',
        width: 100,
      },
      {
        align: 'right',
        dataIndex: 'assetunitnetamount',
        render: (val, rec) => toCurrency(val, rec.currcode ?? localStorage.getItem('currcode') ?? ''),
        width: 150,
      },
      {
        align: 'right',
        dataIndex: 'assetqty',
        width: 100,
      },
      {
        align: 'right',
        dataIndex: 'assetnetamount',
        render: (val, rec) => toCurrency(val, rec.currcode ?? localStorage.getItem('currcode') ?? ''),
        width: 150,
      },
      {
        align: 'right',
        dataIndex: 'rentalpart',
        render: (val, rec) => toCurrency(val, rec.currcode ?? localStorage.getItem('currcode') ?? ''),
        width: 150,
      },
      {
        align: 'right',
        dataIndex: 'assetrvamount',
        render: (val, rec) => toCurrency(val, rec.currcode ?? localStorage.getItem('currcode') ?? ''),
        width: 150,
      },

      {
        align: 'right',
        dataIndex: 'upfront',
        render: (val, rec) => toCurrency(val, rec.currcode ?? localStorage.getItem('currcode') ?? ''),
        width: 120,
      },
      {
        align: 'right',
        dataIndex: 'duration',
        width: 120,
      },
      {
        align: 'right',
        dataIndex: 'rental',
        render: (val, rec) => toCurrency(val, rec.currcode ?? localStorage.getItem('currcode') ?? ''),
        width: 120,
      },
      {
        align: 'right',
        dataIndex: 'assetfinamount',
        render: (val, rec) => toCurrency(val, rec.currcode ?? localStorage.getItem('currcode') ?? ''),
        width: 150,
      },

      {
        dataIndex: 'splitquantity',
        key: 'actions',
        fixed: 'right',
        render: (_, record) => (
          <FormInput name={getName('splitquantity')(record)} noStyle>
            <InputNumber
              disabled={
                ['ORDER', 'DELIVER'].includes(record.assetstatus) ||
                record.assetqty === 1
              }
              min={0}
              max={record.assetqty}
            />
          </FormInput>
        ),
      },
    ],
    [getName]
  );

  return (
    <DF.Provider pagcode="tabSplitListAsset">
      <FormItem
        name="splitassetlist"
        rules={[
          {
            validator(_, value) {
              if (
                isNil(value) ||
                value.every((obj: any) => obj.quantity === 1)
              ) {
                return Promise.reject(
                  new Error('need to split at least one asset')
                );
              }
              return Promise.resolve();
            },
          },
        ]}
        noStyle
      />
      <Form
        form={form}
        onValuesChange={() => {
          formW?.setFieldsValue({
            splitassetlist: form
              .getFieldValue([fieldName])
              .map((asset: any) => ({
                assetid: asset.assetid,
                quantity: asset.splitquantity,
              })),
          });
        }}
      >
        <Spin spinning={isFetching}>
          <Form.Item noStyle shouldUpdate={shouldUpdateCompare([fieldName])}>
            {({ getFieldValue }) => (
              <DataGrid
                title={<GetLabel label="ListAssets" />}
                columns={columns}
                dataSource={getFieldValue(fieldName)}
                rowKey="assetid"
                hideFilters
              />
            )}
          </Form.Item>
        </Spin>
      </Form>
    </DF.Provider>
  );
};
export default TabSplitListAsset;
